class DeliveryChecker extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.delivery-checker__input');
    this.button = this.querySelector('.delivery-checker__btn');
    this.resultContainer = this.querySelector('.delivery-checker__result');
    this.jsonUrl = this.dataset.jsonUrl;
    this.data = null;
    this.isLoading = false;
    this.isChecked = false;

    // Configs from settings
    this.showExpectedDelivery = this.dataset.showExpectedDelivery === 'true';
    this.showFreeShipping = this.dataset.showFreeShipping === 'true';
    this.showCod = this.dataset.showCod === 'true';
    this.supportUrl = this.dataset.supportUrl || '/pages/contact';

    // Text from settings
    this.checkButtonText = this.dataset.checkButtonText || this.button.textContent.trim();
    this.changeButtonText = this.dataset.changeButtonText || 'Change';
    this.deliveringToLabel = this.dataset.deliveringToLabel || 'Delivering to';
    this.freeDeliveryLabel = this.dataset.freeDeliveryLabel || 'Free delivery by';
    this.freeShippingLabel = this.dataset.freeShippingLabel || 'Free Shipping';
    this.codLabel = this.dataset.codLabel || 'Cash on Delivery';
    this.notServiceableText = this.dataset.notServiceableText || 'We currently do not deliver to this PIN Code.';
    this.genericErrorText = this.dataset.genericErrorText || 'Unable to verify delivery at this time. Please try again.';
    this.supportLinkText = this.dataset.supportLinkText || 'Need help? Contact Support';

    const iconTemplate = this.querySelector('.delivery-checker__badge-icon-template');
    this.badgeIconHtml = iconTemplate ? iconTemplate.innerHTML.trim() : '';

    this.bindEvents();
  }

  bindEvents() {
    // Lazy load on first interaction, no auto-search — only the button/Enter trigger a lookup
    this.input.addEventListener('focus', () => this.loadData(), { once: true });
    this.input.addEventListener('input', () => this.loadData());

    // Support enter key
    this.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleButtonClick();
      }
    });

    // Check / Change button
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleButtonClick();
    });
  }

  handleButtonClick() {
    if (this.isChecked) {
      this.reset();
      return;
    }

    if (this.validatePin(this.input.value, true)) {
      this.lookup(this.input.value);
    }
  }

  reset() {
    this.isChecked = false;
    this.input.readOnly = false;
    this.input.value = '';
    this.input.style.color = '';
    this.input.style.fontWeight = '';
    this.input.classList.remove('is-invalid');
    this.button.textContent = this.checkButtonText;
    this.button.style.opacity = '1';
    this.resultContainer.classList.remove('is-active');
    this.resultContainer.innerHTML = '';
    const header = this.querySelector('.delivery-checker__header');
    if (header && this.hasCapturedOriginalHeader) {
      header.innerHTML = this.originalHeaderHtml;
      header.style.display = this.originalHeaderDisplay;
      header.classList.remove('is-badge');
    }
    this.input.focus();
  }

  lock() {
    this.isChecked = true;
    this.input.readOnly = true;
    this.button.textContent = this.changeButtonText;
  }

  async loadData() {
    if (this.data || this.isLoading) return;
    this.isLoading = true;

    // Check sessionStorage first
    const cachedData = sessionStorage.getItem('deliveryCheckerData');
    if (cachedData) {
      this.data = JSON.parse(cachedData);
      this.isLoading = false;
      return;
    }

    try {
      const response = await fetch(this.jsonUrl, { cache: 'force-cache' });
      if (!response.ok) throw new Error('Network response was not ok');
      const json = await response.json();
      this.data = json;
      sessionStorage.setItem('deliveryCheckerData', JSON.stringify(json));
    } catch (error) {
      console.error('Error fetching delivery data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  validatePin(pin, showError = false) {
    const regex = /^[1-9][0-9]{5}$/;
    const invalidSequences = ['000000', '111111', '999999'];
    
    let isValid = true;
    if (!regex.test(pin) || invalidSequences.includes(pin)) {
      isValid = false;
    }

    if (!isValid && showError) {
      this.input.classList.add('is-invalid');
    } else {
      this.input.classList.remove('is-invalid');
    }

    return isValid;
  }

  async lookup(pin) {
    // If still loading JSON, wait a bit
    if (this.isLoading) {
      await new Promise(resolve => setTimeout(resolve, 100));
      return this.lookup(pin);
    }

    if (!this.data) {
      this.resultContainer.innerHTML = `
        <div class="delivery-checker__section">
          <div class="delivery-checker__error-title">${this.genericErrorText}</div>
          <a href="${this.supportUrl}" class="delivery-checker__support-link">${this.supportLinkText} &rarr;</a>
        </div>
      `;
      this.resultContainer.classList.add('is-active');
      this.lock();
      return;
    }

    this.renderLoading();

    // Minimum 300ms loading skeleton animation for premium feel
    await new Promise(resolve => setTimeout(resolve, 300));

    const result = this.data.data ? this.data.data[pin] : null;
    if (result) {
      this.renderSuccess(result);
    } else {
      this.renderError();
    }
    this.lock();
  }

  renderLoading() {
    this.button.textContent = 'VERIFYING...';
    this.button.style.opacity = '0.5';
  }

  renderSuccess(data) {
    const [district, state, distance, cod, serviceable] = data;

    if (!serviceable) {
      this.renderError();
      return;
    }

    // Move Location to a ribbon tucked behind the input's top-left corner
    const header = this.querySelector('.delivery-checker__header');
    if (header) {
      if (!this.hasCapturedOriginalHeader) {
        this.originalHeaderHtml = header.innerHTML;
        this.originalHeaderDisplay = header.style.display;
        this.hasCapturedOriginalHeader = true;
      }
      const labelText = (this.deliveringToLabel || '').trim();
      header.style.display = '';
      header.classList.add('is-badge');
      header.innerHTML = `
        <span class="delivery-checker__badge">
          ${this.badgeIconHtml}
          <span class="delivery-checker__badge-text">${labelText ? labelText + ': ' : ''}${district}, ${state}</span>
        </span>
      `;
    }

    // Move Expected Delivery Date to Input Box
    let deliveryLine = '';
    if (this.showExpectedDelivery) {
      const dateRange = this.calculateDeliveryWindow(distance);
      const freeLabel = (this.freeDeliveryLabel || '').trim();
      deliveryLine = (this.showFreeShipping && freeLabel)
        ? `${freeLabel} ${dateRange}`
        : dateRange;
    } else if (this.showFreeShipping) {
      const freeLabel = (this.freeShippingLabel || '').trim();
      if (freeLabel) deliveryLine = freeLabel;
    }

    if (deliveryLine) {
      this.input.value = deliveryLine;
      this.input.style.color = 'var(--dc-success)';
    }

    let html = '';

    // Cash on Delivery - only show if string is not empty
    const codLabel = (this.codLabel || '').trim();
    if (this.showCod && cod === 1 && codLabel) {
      html += `<div style="color:var(--dc-success); margin-top:12px; font-size:13px;">${codLabel}</div>`;
    }

    this.resultContainer.innerHTML = html;
    if (html.trim() !== '') {
      this.resultContainer.classList.add('is-active');
    } else {
      this.resultContainer.classList.remove('is-active');
    }
    this.button.style.opacity = '1';
  }

  renderError() {
    this.resultContainer.innerHTML = `
      <div class="delivery-checker__section">
        <div class="delivery-checker__error-title">${this.notServiceableText}</div>
        <a href="${this.supportUrl}" class="delivery-checker__support-link">${this.supportLinkText} &rarr;</a>
      </div>
    `;
    this.resultContainer.classList.add('is-active');
    this.button.style.opacity = '1';
  }

  calculateETA(distance) {
    if (distance == null) return [2, 5];
    if (distance <= 150) return [1, 2];
    if (distance <= 350) return [2, 3];
    if (distance <= 700) return [3, 4];
    if (distance <= 1100) return [4, 5];
    if (distance <= 1600) return [5, 6];
    if (distance <= 2100) return [6, 7];
    return [7, 9];
  }

  calculateBusinessDate(startDate, days) {
    let date = new Date(startDate);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      // Skip Sundays (0)
      if (date.getDay() !== 0) {
        added++;
      }
    }
    return date;
  }

  formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  }

  calculateDeliveryWindow(distance) {
    const [minDays, maxDays] = this.calculateETA(distance);
    const today = new Date();
    
    const minDate = this.calculateBusinessDate(today, minDays);
    const maxDate = this.calculateBusinessDate(today, maxDays);
    
    if (minDate.getMonth() === maxDate.getMonth()) {
      return `${minDate.getDate()}–${this.formatDate(maxDate)}`;
    } else {
      return `${this.formatDate(minDate)} – ${this.formatDate(maxDate)}`;
    }
  }
}

customElements.define('delivery-checker', DeliveryChecker);
